import React from 'react';
import { Trash2, Move } from 'lucide-react';
import type { LockedSpotlight } from '../../../types';
import { findRoute, generateSmoothPath } from '../utils/routing';

interface SpotlightOverlayProps {
  isSpotlightActive: boolean;
  spotlightEnabled: boolean;
  status: string;
  customSpotlight: { x: number; y: number; w: number; h: number } | null;
  spotlightRadius: number;
  cursorPos: { x: number; y: number };
  snappedRect: { x: number; y: number; w: number; h: number; rx: number } | null;
  isRightDragging: boolean;
  hoveredSpotlightId: string | null;
  lockedSpotlights: LockedSpotlight[];
  deletePendingSpotlightId: string | null;
  setLockedSpotlights: React.Dispatch<React.SetStateAction<LockedSpotlight[]>>;
  setDeletePendingSpotlightId: (id: string | null) => void;
  setDraggedSpotlightId: (id: string | null) => void;
  draggedSpotlightId: string | null;
  lastCursorPosRef: React.MutableRefObject<{ x: number; y: number }>;
  cursorPosRef: React.MutableRefObject<{ x: number; y: number }>;
  hoveredArrowIndex: number | null;
  setHoveredArrowIndex: (idx: number | null) => void;
  clickHandledByArrowRef: React.MutableRefObject<boolean>;
  insertIntermediateSpotlight: (idx: number, cx: number, cy: number, isIframe: boolean) => void;
  showCursor: boolean;
  iframeObstacles: Array<{ x: number; y: number; w: number; h: number; id: string }>;
}

export const SpotlightOverlay: React.FC<SpotlightOverlayProps> = ({
  isSpotlightActive,
  spotlightEnabled,
  status,
  customSpotlight,
  spotlightRadius,
  cursorPos,
  snappedRect,
  isRightDragging,
  hoveredSpotlightId,
  lockedSpotlights,
  deletePendingSpotlightId,
  setLockedSpotlights,
  setDeletePendingSpotlightId,
  setDraggedSpotlightId,
  draggedSpotlightId,
  lastCursorPosRef,
  cursorPosRef,
  hoveredArrowIndex,
  setHoveredArrowIndex,
  clickHandledByArrowRef,
  insertIntermediateSpotlight,
  showCursor,
  iframeObstacles
}) => {
  const isSpotlightActiveState = isSpotlightActive && spotlightEnabled && (status === 'recording' || status === 'paused');
  
  if (!isSpotlightActiveState) return null;

  const radiusForOpacity = customSpotlight
    ? Math.max(customSpotlight.w, customSpotlight.h) / 2
    : spotlightRadius;

  // Scale base opacity from 0.70 (at r=30) down to 0.40 (at r>=300)
  const baseShadowOpacity = Math.min(0.70, Math.max(0.40, 0.70 - (radiusForOpacity - 30) * (0.30 / 270)));

  return (
    <>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 99990,
        overflow: 'hidden',
        opacity: isSpotlightActiveState ? 1 : 0,
        transition: 'opacity 0.25s ease-in-out',
        willChange: 'opacity',
        background: 'transparent'
      }}>
        <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
          <defs>
            <marker
              id="arrowhead"
              viewBox="0 0 10 10"
              refX="6"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto"
              overflow="visible"
            >
              <path 
                d="M 1 2 L 7 5 L 1 8 Z" 
                fill="rgba(255, 255, 255, 0.95)" 
                style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6))' }}
              />
            </marker>
            <mask id="spotlight-svg-mask">
              <rect width="100%" height="100%" fill="white" />
              
              {isSpotlightActiveState && !isRightDragging && !hoveredSpotlightId && (
                snappedRect ? (
                  <rect 
                    x={snappedRect.x - 4} 
                    y={snappedRect.y - 4} 
                    width={snappedRect.w + 8} 
                    height={snappedRect.h + 8} 
                    rx={snappedRect.rx + 2} 
                    ry={snappedRect.rx + 2} 
                    fill="black" 
                  />
                ) : (
                  <circle cx={cursorPos.x} cy={cursorPos.y} r={spotlightRadius} fill="black" />
                )
              )}

              {customSpotlight && !lockedSpotlights.some(s => s.id === 'temp') && (
                <rect x={customSpotlight.x} y={customSpotlight.y} width={customSpotlight.w} height={customSpotlight.h} rx={12} ry={12} fill="black" />
              )}

              {lockedSpotlights.map(item => (
                item.shape === 'circle' ? (
                  <circle key={item.id} cx={item.x} cy={item.y} r={item.radius} fill="black" />
                ) : (
                  <rect key={item.id} x={item.x} y={item.y} width={item.w} height={item.h} rx={item.radius} ry={item.radius} fill="black" />
                )
              ))}
            </mask>
          </defs>

          <rect width="100%" height="100%" fill={`rgba(0, 0, 0, ${baseShadowOpacity})`} mask="url(#spotlight-svg-mask)" />

          {/* Glow Rings */}
          {isSpotlightActiveState && !isRightDragging && !hoveredSpotlightId && (
            snappedRect ? (
              <rect 
                x={snappedRect.x - 4} 
                y={snappedRect.y - 4} 
                width={snappedRect.w + 8} 
                height={snappedRect.h + 8} 
                rx={snappedRect.rx + 2} 
                ry={snappedRect.rx + 2} 
                stroke="rgba(255, 255, 255, 0.45)" 
                strokeWidth="1.5" 
                fill="none" 
                style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.25))' }}
              />
            ) : (
              <circle 
                cx={cursorPos.x} 
                cy={cursorPos.y} 
                r={spotlightRadius} 
                stroke="rgba(255, 255, 255, 0.45)" 
                strokeWidth="1.5" 
                fill="none" 
                style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.25))' }}
              />
            )
          )}

          {customSpotlight && (
            <rect 
              x={customSpotlight.x} 
              y={customSpotlight.y} 
              width={customSpotlight.w} 
              height={customSpotlight.h} 
              rx={12} 
              ry={12} 
              stroke="rgba(255, 255, 255, 0.45)" 
              strokeWidth="1.5" 
              fill="none" 
              style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.25))' }}
            />
          )}

          {lockedSpotlights.map(item => {
            const isCurrentDragged = draggedSpotlightId === item.id;
            const isDeletePending = deletePendingSpotlightId === item.id;
            
            let strokeColor = "rgba(255, 255, 255, 0.45)";
            let strokeWidth = "1.5";
            let filterStyle = 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.25))';
            
            const isHovered = hoveredSpotlightId === item.id;
            
            if (isDeletePending) {
              strokeColor = "#ef4444";
              strokeWidth = "2.5";
              filterStyle = 'drop-shadow(0 0 8px rgba(239, 68, 68, 0.8))';
            } else if (isCurrentDragged || isHovered) {
              strokeColor = "#38bdf8";
              strokeWidth = "2";
              filterStyle = 'drop-shadow(0 0 6px rgba(56, 189, 248, 0.6))';
            }

            const borderEvents = {
              style: {
                cursor: isCurrentDragged ? 'grabbing' : 'grab',
                pointerEvents: 'stroke' as const
              },
              onMouseDown: (e: React.MouseEvent) => {
                if (e.button === 0) {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isDeletePending) {
                    setLockedSpotlights(prev => {
                      const next = prev.filter(s => s.id !== item.id);
                      return next.map((s, idx) => ({ ...s, sequence: idx + 1 }));
                    });
                    setDeletePendingSpotlightId(null);
                  } else {
                    setDraggedSpotlightId(item.id);
                    lastCursorPosRef.current = { ...cursorPosRef.current };
                  }
                }
              }
            };
            
            return item.shape === 'circle' ? (
              <g key={`border-group-${item.id}`}>
                <circle 
                  cx={item.x} 
                  cy={item.y} 
                  r={item.radius} 
                  stroke={strokeColor} 
                  strokeWidth={strokeWidth} 
                  fill="none" 
                  style={{ 
                    filter: filterStyle,
                    pointerEvents: 'none'
                  }}
                />
                <circle 
                  cx={item.x} 
                  cy={item.y} 
                  r={item.radius} 
                  stroke="transparent" 
                  strokeWidth="16" 
                  fill="none" 
                  {...borderEvents}
                />
              </g>
            ) : (
              <g key={`border-group-${item.id}`}>
                <rect 
                  x={item.x} 
                  y={item.y} 
                  width={item.w} 
                  height={item.h} 
                  rx={item.radius} 
                  ry={item.radius} 
                  stroke={strokeColor} 
                  strokeWidth={strokeWidth} 
                  fill="none" 
                  style={{ 
                    filter: filterStyle,
                    pointerEvents: 'none'
                  }}
                />
                <rect 
                  x={item.x} 
                  y={item.y} 
                  width={item.w} 
                  height={item.h} 
                  rx={item.radius} 
                  ry={item.radius} 
                  stroke="transparent" 
                  strokeWidth="16" 
                  fill="none" 
                  {...borderEvents}
                />
              </g>
            );
          })}

          {/* Sequential arrows */}
          {(() => {
            const sorted = [...lockedSpotlights].sort((a, b) => a.sequence - b.sequence);
            const paths: React.ReactNode[] = [];
            
            const W = window.innerWidth;
            const H = status === 'recording' ? window.innerHeight : window.innerHeight - 56;
            const y_offset = status === 'recording' ? 0 : 56;

            const getClosestPoints = (a: LockedSpotlight, b: LockedSpotlight) => {
              const a_x_min = a.shape === 'circle' ? a.x - a.radius : a.x;
              const a_x_max = a.shape === 'circle' ? a.x + a.radius : a.x + a.w;
              const a_y_min = a.shape === 'circle' ? a.y - a.radius : a.y;
              const a_y_max = a.shape === 'circle' ? a.y + a.radius : a.y + a.h;

              const b_x_min = b.shape === 'circle' ? b.x - b.radius : b.x;
              const b_x_max = b.shape === 'circle' ? b.x + b.radius : b.x + b.w;
              const b_y_min = b.shape === 'circle' ? b.y - b.radius : b.y;
              const b_y_max = b.shape === 'circle' ? b.y + b.radius : b.y + b.h;

              let xa_min = 0, xb_min = 0;
              if (b_x_min > a_x_max) {
                xa_min = a_x_max;
                xb_min = b_x_min;
              } else if (a_x_min > b_x_max) {
                xa_min = a_x_min;
                xb_min = (b.shape === 'square') ? b_x_min : b_x_max;
              } else {
                const overlapStart = Math.max(a_x_min, b_x_min);
                const overlapEnd = Math.min(a_x_max, b_x_max);
                const mid = (overlapStart + overlapEnd) / 2;
                xa_min = mid;
                xb_min = mid;
              }

              let ya_min = 0, yb_min = 0;
              if (b_y_min > a_y_max) {
                ya_min = a_y_max;
                yb_min = b_y_min;
              } else if (a_y_min > b_y_max) {
                ya_min = a_y_min;
                yb_min = b_y_max;
              } else {
                const overlapStart = Math.max(a_y_min, b_y_min);
                const overlapEnd = Math.min(a_y_max, b_y_max);
                const mid = (overlapStart + overlapEnd) / 2;
                ya_min = mid;
                yb_min = mid;
              }

              return [
                { name: 'top-top', pA: { x: xa_min, y: a_y_min, vx: 0, vy: -1 }, pB: { x: xb_min, y: b_y_min, vx: 0, vy: -1 } },
                { name: 'bottom-bottom', pA: { x: xa_min, y: a_y_max, vx: 0, vy: 1 }, pB: { x: xb_min, y: b_y_max, vx: 0, vy: 1 } },
                { name: 'left-left', pA: { x: a_x_min, y: ya_min, vx: -1, vy: 0 }, pB: { x: b_x_min, y: yb_min, vx: -1, vy: 0 } },
                { name: 'right-right', pA: { x: a_x_max, y: ya_min, vx: 1, vy: 0 }, pB: { x: b_x_max, y: yb_min, vx: 1, vy: 0 } },
                { name: 'right-left', pA: { x: a_x_max, y: ya_min, vx: 1, vy: 0 }, pB: { x: b_x_min, y: yb_min, vx: -1, vy: 0 } },
                { name: 'left-right', pA: { x: a_x_min, y: ya_min, vx: -1, vy: 0 }, pB: { x: b_x_max, y: yb_min, vx: 1, vy: 0 } },
                { name: 'bottom-top', pA: { x: xa_min, y: a_y_max, vx: 0, vy: 1 }, pB: { x: xb_min, y: b_y_min, vx: 0, vy: -1 } },
                { name: 'top-bottom', pA: { x: xa_min, y: a_y_min, vx: 0, vy: -1 }, pB: { x: xb_min, y: b_y_max, vx: 0, vy: 1 } },
                { name: 'bottom-left', pA: { x: xa_min, y: a_y_max, vx: 0, vy: 1 }, pB: { x: b_x_min, y: yb_min, vx: -1, vy: 0 } },
                { name: 'top-left', pA: { x: xa_min, y: a_y_min, vx: 0, vy: -1 }, pB: { x: b_x_min, y: yb_min, vx: -1, vy: 0 } },
                { name: 'bottom-right', pA: { x: xa_min, y: a_y_max, vx: 0, vy: 1 }, pB: { x: b_x_max, y: yb_min, vx: 1, vy: 0 } },
                { name: 'top-right', pA: { x: xa_min, y: a_y_min, vx: 0, vy: -1 }, pB: { x: b_x_max, y: yb_min, vx: 1, vy: 0 } },
                { name: 'left-top', pA: { x: a_x_min, y: ya_min, vx: -1, vy: 0 }, pB: { x: xb_min, y: b_y_min, vx: 0, vy: -1 } },
                { name: 'right-top', pA: { x: a_x_max, y: ya_min, vx: 1, vy: 0 }, pB: { x: xb_min, y: b_y_min, vx: 0, vy: -1 } },
                { name: 'left-bottom', pA: { x: a_x_min, y: ya_min, vx: -1, vy: 0 }, pB: { x: xb_min, y: b_y_max, vx: 0, vy: 1 } },
                { name: 'right-bottom', pA: { x: a_x_max, y: ya_min, vx: 1, vy: 0 }, pB: { x: xb_min, y: b_y_max, vx: 0, vy: 1 } }
              ];
            };

            const getExitSide = (curr: LockedSpotlight, next: LockedSpotlight) => {
              const currCenter = {
                x: curr.shape === 'circle' ? curr.x : curr.x + (curr.w || 0) / 2,
                y: curr.shape === 'circle' ? curr.y : curr.y + (curr.h || 0) / 2
              };
              const nextCenter = {
                x: next.shape === 'circle' ? next.x : next.x + (next.w || 0) / 2,
                y: next.shape === 'circle' ? next.y : next.y + (next.h || 0) / 2
              };
              
              const dx = nextCenter.x - currCenter.x;
              const dy = nextCenter.y - currCenter.y;
              
              if (Math.abs(dx) > Math.abs(dy)) {
                return dx > 0 ? 'right' : 'left';
              } else {
                return dy > 0 ? 'bottom' : 'top';
              }
            };

            for (let i = 0; i < sorted.length - 1; i++) {
              const a = sorted[i];
              const b = sorted[i + 1];
              
              const centerA = { x: a.shape === 'circle' ? a.x : a.x + a.w / 2, y: a.shape === 'circle' ? a.y : a.y + a.h / 2 };
              const centerB = { x: b.shape === 'circle' ? b.x : b.x + b.w / 2, y: b.shape === 'circle' ? b.y : b.y + b.h / 2 };
              
              const centerDx = centerB.x - centerA.x;
              const centerDy = centerB.y - centerA.y;
              
              const a_x_min = a.shape === 'circle' ? a.x - a.radius : a.x;
              const a_x_max = a.shape === 'circle' ? a.x + a.radius : a.x + a.w;
              const a_y_min = a.shape === 'circle' ? a.y - a.radius : a.y;
              const a_y_max = a.shape === 'circle' ? a.y + a.radius : a.y + a.h;
              const b_x_min = b.shape === 'circle' ? b.x - b.radius : b.x;
              const b_x_max = b.shape === 'circle' ? b.x + b.radius : b.x + b.w;
              const b_y_min = b.shape === 'circle' ? b.y - b.radius : b.y;
              const b_y_max = b.shape === 'circle' ? b.y + b.radius : b.y + b.h;

              const gapX = Math.max(0, Math.max(a_x_min, b_x_min) - Math.min(a_x_max, b_x_max));
              const gapY = Math.max(0, Math.max(a_y_min, b_y_min) - Math.min(a_y_max, b_y_max));
              const boundaryDist = Math.sqrt(gapX * gapX + gapY * gapY);

              const options = getClosestPoints(a, b);
              let filteredOptions = options;
              
              if (boundaryDist < 120) {
                const overlapX = Math.max(0, Math.min(a_x_max, b_x_max) - Math.max(a_x_min, b_x_min));
                const overlapY = Math.max(0, Math.min(a_y_max, b_y_max) - Math.max(a_y_min, b_y_min));
                
                let isHorizontal = false;
                if (overlapY > 0 && overlapX === 0) {
                  isHorizontal = true;
                } else if (overlapX > 0 && overlapY === 0) {
                  isHorizontal = false;
                } else {
                  isHorizontal = Math.abs(centerDx) > Math.abs(centerDy);
                }

                if (isHorizontal) {
                  filteredOptions = options.filter(o => o.name === 'top-top');
                } else {
                  filteredOptions = options.filter(o => o.name === 'left-left' || o.name === 'right-right');
                }
              }

              if (i + 2 < sorted.length) {
                const b_next = sorted[i + 2];
                const b_exit_side = getExitSide(b, b_next);
                
                const nonCollidingOptions = filteredOptions.filter(opt => {
                  const parts = opt.name.split('-');
                  const sideB = parts[1];
                  return sideB !== b_exit_side;
                });
                
                if (nonCollidingOptions.length > 0) {
                  filteredOptions = nonCollidingOptions;
                }
              }
              
              let blockTopA = false, blockBottomA = false, blockLeftA = false, blockRightA = false;
              let blockTopB = false, blockBottomB = false, blockLeftB = false, blockRightB = false;
              
              for (const c of lockedSpotlights) {
                if (c.id === a.id || c.id === b.id) continue;
                
                const cx_min = c.shape === 'circle' ? c.x - c.radius : c.x;
                const cx_max = c.shape === 'circle' ? c.x + c.radius : c.x + (c.w || 0);
                const cy_min = c.shape === 'circle' ? c.y - c.radius : c.y;
                const cy_max = c.shape === 'circle' ? c.y + c.radius : c.y + (c.h || 0);
                
                const overlapXA = Math.max(cx_min, a_x_min) < Math.min(cx_max, a_x_max);
                const overlapYA = Math.max(cy_min, a_y_min) < Math.min(cy_max, a_y_max);
                
                if (overlapXA) {
                  if (cy_max <= a_y_min && a_y_min - cy_max < 60) blockTopA = true;
                  if (cy_min >= a_y_max && cy_min - a_y_max < 60) blockBottomA = true;
                }
                if (overlapYA) {
                  if (cx_max <= a_x_min && a_x_min - cx_max < 60) blockLeftA = true;
                  if (cx_min >= a_x_max && cx_min - a_x_max < 60) blockRightA = true;
                }
                
                const overlapXB = Math.max(cx_min, b_x_min) < Math.min(cx_max, b_x_max);
                const overlapYB = Math.max(cy_min, b_y_min) < Math.min(cy_max, b_y_max);
                
                if (overlapXB) {
                  if (cy_max <= b_y_min && b_y_min - cy_max < 60) blockTopB = true;
                  if (cy_min >= b_y_max && cy_min - b_y_max < 60) blockBottomB = true;
                }
                if (overlapYB) {
                  if (cx_max <= b_x_min && b_x_min - cx_max < 60) blockLeftB = true;
                  if (cx_min >= b_x_max && cx_min - b_x_max < 60) blockRightB = true;
                }
              }
              
              const nonBlockedOptions = filteredOptions.filter(opt => {
                const parts = opt.name.split('-');
                const sideA = parts[0];
                const sideB = parts[1];
                
                if (sideA === 'top' && blockTopA) return false;
                if (sideA === 'bottom' && blockBottomA) return false;
                if (sideA === 'left' && blockLeftA) return false;
                if (sideA === 'right' && blockRightA) return false;
                
                if (sideB === 'top' && blockTopB) return false;
                if (sideB === 'bottom' && blockBottomB) return false;
                if (sideB === 'left' && blockLeftB) return false;
                if (sideB === 'right' && blockRightB) return false;
                
                return true;
              });
              
              if (nonBlockedOptions.length > 0) {
                filteredOptions = nonBlockedOptions;
              }
              
              let minD = Infinity;
              let bestOption = options[0];
              
              const listOptions = filteredOptions.length > 0 ? filteredOptions : options;
              for (const opt of listOptions) {
                const dx = opt.pB.x - opt.pA.x;
                const dy = opt.pB.y - opt.pA.y;
                const d = Math.sqrt(dx * dx + dy * dy);
                if (d < minD) {
                  minD = d;
                  bestOption = opt;
                }
              }
              
              const routePoints = findRoute(a, b, bestOption, lockedSpotlights, W, H, y_offset, iframeObstacles);
              const pathD = generateSmoothPath(routePoints, bestOption);
              
              const isHovered = hoveredArrowIndex === i;
              
              paths.push(
                <g key={`arrow-group-${a.id}-${b.id}`}>
                  <path
                     d={pathD}
                     stroke={isHovered ? "#38bdf8" : "rgba(255, 255, 255, 0.85)"}
                     strokeWidth={isHovered ? "5" : "3.5"}
                     fill="none"
                     strokeDasharray="8, 6"
                     markerEnd="url(#arrowhead)"
                     style={{ 
                       animation: 'flow 0.8s linear infinite',
                       transition: 'stroke 0.2s, strokeWidth 0.2s',
                       filter: isHovered 
                         ? 'drop-shadow(0 0 6px rgba(56, 189, 248, 0.85)) drop-shadow(0 2px 3px rgba(0, 0, 0, 0.6))' 
                         : 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.75))',
                       pointerEvents: 'none'
                     }}
                  />
                  <path
                     d={pathD}
                     stroke="transparent"
                     strokeWidth="24"
                     fill="none"
                     style={{ 
                       cursor: 'pointer',
                       pointerEvents: 'stroke' 
                     }}
                     onMouseEnter={() => setHoveredArrowIndex(i)}
                     onMouseLeave={() => setHoveredArrowIndex(null)}
                     onMouseDown={(e) => {
                       if (e.button === 2) {
                         e.preventDefault();
                         e.stopPropagation();
                         clickHandledByArrowRef.current = true;
                       }
                     }}
                     onMouseUp={(e) => {
                       if (e.button === 2) {
                         e.preventDefault();
                         e.stopPropagation();
                         clickHandledByArrowRef.current = true;
                         insertIntermediateSpotlight(i, e.clientX, e.clientY, (e.view as any) !== window);
                       }
                     }}
                  />
                </g>
              );
            }
            return paths;
          })()}
        </svg>

        {/* Sequence badges */}
        {lockedSpotlights.map(item => {
          let left = 0;
          let top = 0;
          
          if (item.shape === 'circle') {
            const r = item.radius;
            left = item.x + r * 0.707 - 16;
            top = item.y - r * 0.707 - 16;
          } else {
            left = item.x + item.w - 36;
            top = item.y - 16;
          }
          
          const isDeletePending = deletePendingSpotlightId === item.id;
          
          return (
            <div 
              key={`badge-${item.id}`}
              style={{
                position: 'absolute',
                left: left,
                top: top,
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: isDeletePending ? '#ef4444' : '#38bdf8',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1rem',
                fontWeight: 900,
                boxShadow: isDeletePending 
                  ? '0 3px 8px rgba(239, 68, 68, 0.6), 0 0 0 2px rgba(255, 255, 255, 0.9)'
                  : '0 3px 8px rgba(56, 189, 248, 0.5), 0 0 0 2px rgba(255, 255, 255, 0.9)',
                pointerEvents: 'auto',
                cursor: isDeletePending ? 'pointer' : (draggedSpotlightId === item.id ? 'grabbing' : 'grab'),
                zIndex: 99991,
                animation: 'scaleUp 0.2s ease-out'
              }}
              onMouseDown={(e) => {
                if (e.button === 0) {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isDeletePending) {
                    setLockedSpotlights(prev => {
                      const next = prev.filter(s => s.id !== item.id);
                      return next.map((s, idx) => ({ ...s, sequence: idx + 1 }));
                    });
                    setDeletePendingSpotlightId(null);
                  } else {
                    setDraggedSpotlightId(item.id);
                    lastCursorPosRef.current = { ...cursorPosRef.current };
                  }
                }
              }}
            >
              {isDeletePending ? (
                <Trash2 size={16} />
              ) : (
                draggedSpotlightId === item.id ? (
                  <Move size={16} />
                ) : (
                  item.sequence
                )
              )}
            </div>
          );
        })}
      </div>

      {/* Laser pointer */}
      {isSpotlightActive && spotlightEnabled && (status === 'recording' || status === 'paused') && showCursor && (
        <div style={{
          position: 'absolute',
          left: 0,
          top: 0,
          transform: `translate3d(${cursorPos.x - 3}px, ${cursorPos.y - 3}px, 0)`,
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: '#ff3366',
          boxShadow: '0 0 8px #ff3366, 0 0 0 1px rgba(255, 255, 255, 0.8)',
          pointerEvents: 'none',
          zIndex: 99992,
          transition: 'transform 0s'
        }} />
      )}
    </>
  );
};
